import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { lightTheme, darkTheme } from "../styles/theme";
import { FaGithub, FaLinkedin, FaEnvelope } from "react-icons/fa";
import { IconType } from "react-icons";
import { HiArrowRight } from "react-icons/hi";
import { RandomFloatingIcons, AI_ICONS } from "./TechStackIcons";

interface ContactItem {
  title: string;
  description: string;
  link: string;
  icon: IconType;
  accent: string;
}

const ContactSection = styled.section<{ theme: "light" | "dark" }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 70px 5% 40px;
  text-align: center;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${({ theme }) =>
    theme === "light" ? "#ffffff" : darkTheme.surface};

  /* Cross-hatch pattern */
  background-image: ${({ theme }) =>
    theme === "light"
      ? `
        repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(77, 159, 255, 0.18) 10px,
          rgba(77, 159, 255, 0.18) 12px
        ),
        repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 10px,
          rgba(77, 159, 255, 0.18) 10px,
          rgba(77, 159, 255, 0.18) 12px
        )
      `
      : `
        repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(255, 255, 255, 0.03) 10px,
          rgba(255, 255, 255, 0.03) 11px
        ),
        repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 10px,
          rgba(255, 255, 255, 0.03) 10px,
          rgba(255, 255, 255, 0.03) 11px
        )
      `};

  /* Central accent glow */
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50%;
    height: 50%;
    background: ${({ theme }) =>
      theme === "light"
        ? `radial-gradient(ellipse at center, rgba(77, 159, 255, 0.15), transparent 70%)`
        : `radial-gradient(ellipse at center, ${darkTheme.accent}12, transparent 70%)`};
    pointer-events: none;
    z-index: 0;
  }

  @media (min-width: 768px) {
    padding: 70px 10% 40px;
  }
`;

const ContentWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)<{ theme: "light" | "dark" }>`
  font-size: 1.5rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.background};
  margin-bottom: 1rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  display: inline-block;
  padding: 8px 16px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  transform: rotate(-2deg);

  @media (min-width: 768px) {
    font-size: 1.8rem;
  }

  @media (min-width: 1024px) {
    font-size: 2rem;
  }
`;

const Description = styled(motion.p)<{ theme: "light" | "dark" }>`
  max-width: 700px;
  margin: 1rem auto 1.2rem;
  line-height: 1.5;
  font-size: 0.8rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 0.9rem;
    margin: 1.2rem auto 1.5rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.95rem;
    margin-bottom: 1.8rem;
  }

  strong {
    font-weight: 900;
    color: ${({ theme }) =>
      theme === "light" ? lightTheme.text : darkTheme.text};
  }
`;

const ContactCards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  width: 100%;
  max-width: 1200px;
  margin-top: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-top: 1.2rem;
  }
`;

const ContactCard = styled(motion.a)<{
  theme: "light" | "dark";
  accent: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px 14px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  text-decoration: none;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translate(-5px, -5px);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? `15px 15px 0 ${lightTheme.border}`
        : `15px 15px 0 ${darkTheme.accent}`};
  }

  &:active {
    transform: translate(0, 0);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  }

  @media (min-width: 768px) {
    padding: 22px 18px;
  }

  @media (min-width: 1024px) {
    padding: 26px 22px;
  }
`;

const IconWrapper = styled.div<{ theme: "light" | "dark" }>`
  font-size: 1.8rem;
  margin-bottom: 0.7rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  padding: 12px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.border : darkTheme.border};
  border-radius: 50%;
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  @media (min-width: 768px) {
    font-size: 2rem;
    width: 56px;
    height: 56px;
    margin-bottom: 0.9rem;
  }

  @media (min-width: 1024px) {
    font-size: 2.3rem;
    width: 62px;
    height: 62px;
  }
`;

const CardTitle = styled.h3<{ theme: "light" | "dark" }>`
  font-size: 0.95rem;
  margin-bottom: 0.4rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (min-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 0.5rem;
  }

  @media (min-width: 1024px) {
    font-size: 1.15rem;
  }
`;

const CardDescription = styled.p<{ theme: "light" | "dark" }>`
  font-size: 0.7rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  font-weight: 700;
  opacity: 0.9;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 0.75rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.8rem;
  }
`;

const ArrowIcon = styled(motion.div)<{ theme: "light" | "dark" }>`
  position: absolute;
  bottom: 10px;
  right: 10px;
  font-size: 1.1rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  opacity: 0.8;

  @media (min-width: 768px) {
    bottom: 12px;
    right: 12px;
    font-size: 1.3rem;
  }

  @media (min-width: 1024px) {
    font-size: 1.5rem;
  }
`;

const CTABox = styled(motion.div)<{ theme: "light" | "dark" }>`
  margin-top: 18px;
  padding: 16px 14px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  transform: rotate(1deg);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: rotate(-1deg) scale(1.02);
  }

  @media (min-width: 768px) {
    padding: 20px 18px;
    margin-top: 22px;
  }

  @media (min-width: 1024px) {
    padding: 24px 22px;
    margin-top: 26px;
  }
`;

const CTAText = styled.p<{ theme: "light" | "dark" }>`
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.4;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  margin: 0;
  position: relative;
  z-index: 1;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }

  @media (min-width: 1024px) {
    font-size: 1rem;
  }

  .emoji {
    font-size: 1.3em;
    margin: 0 6px;
  }
`;

const Contact: React.FC = () => {
  const { theme } = useTheme();
  const [emailData, setEmailData] = useState({ display: "", link: "" });

  // Obfuscate email to protect from spam harvesters
  useEffect(() => {
    // Base64 encoded email: thuan2172001@gmail.com
    const encodedEmail = "dGh1YW4yMTcyMDAxQGdtYWlsLmNvbQ==";
    const decodedEmail = atob(encodedEmail);
    setEmailData({
      display: decodedEmail,
      link: `mailto:${decodedEmail}`,
    });
  }, []);

  const contactItems: ContactItem[] = [
    {
      title: "Email",
      description: emailData.display || "Loading...",
      link: emailData.link || "#",
      icon: FaEnvelope,
      accent: theme === "light" ? lightTheme.accent : darkTheme.accent,
    },
    {
      title: "GitHub",
      description: "github.com/thuan2172001",
      link: "https://github.com/thuan2172001",
      icon: FaGithub,
      accent: theme === "light" ? lightTheme.accent : darkTheme.accent,
    },
    {
      title: "LinkedIn",
      description: "linkedin.com/in/thuan-trinh-van",
      link: "https://linkedin.com/in/thuan-trinh-van",
      icon: FaLinkedin,
      accent: theme === "light" ? lightTheme.accent : darkTheme.accent,
    },
  ];

  return (
    <ContactSection id="contact" theme={theme}>
      {/* Floating Tech Stack Icons - AI Tools */}
      <RandomFloatingIcons theme={theme} icons={AI_ICONS} count={3} />

      <ContentWrapper>
        <SectionTitle
          theme={theme}
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Let's Connect!
        </SectionTitle>
        <Description
          theme={theme}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          I'm always interested in hearing about{" "}
          <strong>new opportunities</strong> and{" "}
          <strong>innovative projects</strong>. Whether you have a question or
          just want to say hi, I'll try my best to get back to you!
        </Description>
        <ContactCards>
          {contactItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <ContactCard
                key={item.title}
                theme={theme}
                accent={item.accent}
                href={item.link}
                target={item.title !== "Email" ? "_blank" : undefined}
                rel={item.title !== "Email" ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <IconWrapper theme={theme}>
                  <Icon />
                </IconWrapper>
                <CardTitle theme={theme}>{item.title}</CardTitle>
                <CardDescription theme={theme}>
                  {item.description}
                </CardDescription>
                <ArrowIcon
                  theme={theme}
                  initial={{ x: 0 }}
                  whileHover={{ x: 5 }}
                >
                  <HiArrowRight />
                </ArrowIcon>
              </ContactCard>
            );
          })}
        </ContactCards>

        <CTABox
          theme={theme}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <CTAText theme={theme}>
            Let's build something <strong>AMAZING</strong> together!
          </CTAText>
        </CTABox>
      </ContentWrapper>
    </ContactSection>
  );
};

export default Contact;
